import * as FileSystem from 'expo-file-system';
import { Platform } from 'react-native';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const CHUNK_SIZE = 1024 * 1024; // 1MB chunks for large files

const MIME_TYPE_MAPPING = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

  txt: 'text/plain',
  csv: 'text/csv',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
};

function getMimeType(uri) {
  const extension = uri.split('.').pop()?.toLowerCase();
  return MIME_TYPE_MAPPING[extension] || 'application/octet-stream';
}

async function readFileAsBase64Chunked(uri) {
  try {
    const fileInfo = await FileSystem.getInfoAsync(uri);
    if (!fileInfo.exists) {
      throw new Error('File does not exist');
    }

    const fileSize = fileInfo.size;
    let base64String = '';
    let offset = 0;

    while (offset < fileSize) {
      const options = {
        encoding: FileSystem.EncodingType.Base64,
        position: offset,
        length: Math.min(CHUNK_SIZE, fileSize - offset),
      };

      const chunk = await FileSystem.readAsStringAsync(uri, options);
      base64String += chunk;
      offset += CHUNK_SIZE;
    }

    return base64String;
  } catch (error) {
    throw new Error(`Failed to read file in chunks: ${error.message}`);
  }
}

async function convertFileToBase64Web(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const result = reader.result;
        const mimeType =
          file.mimeType || file.type || getMimeType(file.name || '');

        resolve({
          success: true,
          dataUrl: result,
          mimeType,
          size: file.size,
          base64: result.split(',')[1],
        });
      } catch (error) {
        resolve({
          success: false,
          error: error.message || 'Failed to convert file to base64',
        });
      }
    };

    reader.onerror = () => {
      resolve({
        success: false,
        error: 'Failed to read file',
      });
    };

    // Handle different URI types on web
    if (file.uri && file.uri.startsWith('blob:')) {
      // Handle blob URIs
      fetch(file.uri)
        .then((response) => response.blob())
        .then((blob) => {
          // Check file size
          if (blob.size > MAX_FILE_SIZE) {
            resolve({
              success: false,
              error: `File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
            });
            return;
          }

          reader.readAsDataURL(blob);
        })
        .catch((error) => {
          resolve({
            success: false,
            error: 'Failed to access file',
          });
        });
    } else if (file.uri && file.uri.startsWith('data:')) {
      // File is already in base64 data URL format, just return it
      const mimeType =
        file.mimeType || file.type || file.uri.split(';')[0].split(':')[1];

      resolve({
        success: true,
        dataUrl: file.uri,
        mimeType,
        size: file.size || 0,
        base64: file.uri.split(',')[1],
      });
    } else {
      resolve({
        success: false,
        error: 'Unsupported file URI format for web platform',
      });
    }
  });
}

async function convertFileToBase64Native(file) {
  if (!file || !file.uri) {
    throw new Error('Invalid file object');
  }

  const fileInfo = await FileSystem.getInfoAsync(file.uri);

  if (!fileInfo.exists) {
    throw new Error('File does not exist');
  }

  if (fileInfo.size > MAX_FILE_SIZE) {
    throw new Error(
      `File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`
    );
  }

  let base64String;

  if (fileInfo.size > CHUNK_SIZE) {
    base64String = await readFileAsBase64Chunked(file.uri);
  } else {
    base64String = await FileSystem.readAsStringAsync(file.uri, {
      encoding: FileSystem.EncodingType.Base64,
    });
  }

  const mimeType = file.mimeType || file.type || getMimeType(file.uri);
  const dataUrl = `data:${mimeType};base64,${base64String}`;

  return {
    success: true,
    dataUrl,
    mimeType,
    size: fileInfo.size,
    base64: base64String,
  };
}

export async function convertFileToBase64(file) {
  try {
    // Check file size first if available
    if (file.size && file.size > MAX_FILE_SIZE) {
      return {
        success: false,
        error: `File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
      };
    }

    if (Platform.OS === 'web') {
      return await convertFileToBase64Web(file);
    } else {
      return await convertFileToBase64Native(file);
    }
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to convert file to base64',
    };
  }
}

export async function convertMultipleFiles(files) {
  try {
    const conversionPromises = files.map((file) => convertFileToBase64(file));
    const results = await Promise.all(conversionPromises);

    const succeeded = results.filter((r) => r.success);
    const failed = results.filter((r) => !r.success);

    return {
      succeeded,
      failed,
      hasErrors: failed.length > 0,
    };
  } catch (error) {
    console.error('Multiple file conversion error:', error);
    return {
      succeeded: [],
      failed: files.map(() => ({ success: false, error: error.message })),
      hasErrors: true,
    };
  }
}

export function validateFileSize(file) {
  if (!file || !file.size) {
    return {
      valid: false,
      error: 'Invalid file object',
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
    };
  }

  return {
    valid: true,
    size: file.size,
  };
}

export function getFileExtension(uri) {
  return uri.split('.').pop()?.toLowerCase() || '';
}

export function isImageFile(uri) {
  const extension = getFileExtension(uri);
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension);
}

export function isPDFFile(uri) {
  const extension = getFileExtension(uri);
  return extension === 'pdf';
}

export function isDocumentFile(uri) {
  const extension = getFileExtension(uri);
  return ['doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'].includes(extension);
}

export const FILE_UPLOAD_CONFIG = {
  MAX_FILE_SIZE,
  CHUNK_SIZE,
  SUPPORTED_MIME_TYPES: Object.values(MIME_TYPE_MAPPING),
  SUPPORTED_EXTENSIONS: Object.keys(MIME_TYPE_MAPPING),
};
